import React, { useState, useEffect } from 'react';
import { Trash2, Search, AlertTriangle, CheckSquare, Square, Calendar, Loader2 } from 'lucide-react';
import { Table } from '@/components';
import { orderApi } from '@/services/orderApi';
import { Order } from '@/types/order';
import { auth } from '@/services/firebase';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import toast from 'react-hot-toast';

const STATUS_LABELS: Record<string, string> = {
  awaiting_payment: 'Ödeme Bekleniyor',
  pending: 'Beklemede',
  confirmed: 'Onaylandı',
  preparing: 'Hazırlanıyor',
  on_the_way: 'Kurye Yolda',
  delivered: 'Teslim Edildi',
  cancelled: 'İptal',
};

const STATUS_COLORS: Record<string, string> = {
  awaiting_payment: 'bg-orange-100 text-orange-800',
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-indigo-100 text-indigo-800',
  preparing: 'bg-blue-100 text-blue-800',
  on_the_way: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const getDateString = (daysOffset: number = 0): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return date.toISOString().split('T')[0];
};

export const BulkDeleteOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>(getDateString(-30));
  const [endDate, setEndDate] = useState<string>(getDateString(1));
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Results modal state
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [deleteResults, setDeleteResults] = useState<any>(null);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await orderApi.getOrders({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        searchQuery: searchQuery || undefined,
        limit: 500,
      });
      setOrders(response.orders || []);
    } catch (error) {
      toast.error('Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [statusFilter, startDate, endDate, searchQuery]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === orders.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(orders.map(o => o.id)));
    }
  };

  const handleDeleteClick = () => {
    if (selectedIds.size === 0) {
      toast.error('Silmek için en az bir sipariş seçin');
      return;
    }
    setPassword('');
    setPasswordError('');
    setShowPasswordModal(true);
  };

  const handlePasswordConfirm = async () => {
    if (!password) {
      setPasswordError('Şifre gerekli');
      return;
    }

    setVerifying(true);
    setPasswordError('');

    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        setPasswordError('Oturum bilgisi bulunamadı');
        return;
      }

      // Re-authenticate with Firebase
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      // Password verified, proceed with deletion
      setShowPasswordModal(false);
      await performBulkDelete();
    } catch (error: any) {
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setPasswordError('Şifre yanlış');
      } else {
        setPasswordError('Doğrulama hatası: ' + (error.message || 'Bilinmeyen hata'));
      }
    } finally {
      setVerifying(false);
    }
  };

  const performBulkDelete = async () => {
    setDeleting(true);
    try {
      const idsToDelete = Array.from(selectedIds);
      const response = await orderApi.bulkDeleteOrders(idsToDelete);

      setDeleteResults(response.results);
      setShowResultsModal(true);

      // Remove deleted orders from the list
      setOrders(prev => prev.filter(o => !selectedIds.has(o.id)));
      setSelectedIds(new Set());

      toast.success(response.message);
    } catch (error: any) {
      toast.error(error.message || 'Silme işlemi başarısız');
    } finally {
      setDeleting(false);
    }
  };

  const allSelected = orders.length > 0 && selectedIds.size === orders.length;

  const tableData = orders.map((order) => ({
    id: order.id,
    selected: selectedIds.has(order.id),
    orderNo: order.orderNo || '-',
    recipient: order.recipientInfo?.fullName || 'Misafir',
    sender: order.senderInfo?.name || '-',
    total: `₺${order.totalPrice?.toFixed(2) || '0.00'}`,
    status: STATUS_LABELS[order.status] || order.status,
    statusRaw: order.status,
    date: new Date(order.createdAt).toLocaleDateString('tr-TR'),
    itemCount: `${order.items?.length || 0} ürün`,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-dark dark:text-light">Toplu Sipariş Silme</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Siparişleri seçerek toplu silme işlemi yapın. İlişkili tüm kayıtlar da silinir.
          </p>
        </div>
        <button
          onClick={handleDeleteClick}
          disabled={selectedIds.size === 0 || deleting}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {deleting ? (
            <Loader2 className="animate-spin" size={18} />
          ) : (
            <Trash2 size={18} />
          )}
          {deleting ? 'Siliniyor...' : `Seçilenleri Sil (${selectedIds.size})`}
        </button>
      </div>

      {/* Warning */}
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="text-red-800 dark:text-red-200 font-medium">Dikkat: Bu işlem geri alınamaz!</p>
          <p className="text-red-600 dark:text-red-400 text-sm mt-1">
            Silinen siparişlerle birlikte şunlar da kalıcı olarak silinir: finansal kayıtlar, ödeme işlemleri, muhasebe logları, bildirimler, kupon kullanım kayıtları ve depolama dosyaları (önizleme & teslimat fotoğrafları).
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Ara (Gönderen/Alıcı/Email/Telefon)</label>
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Gönderen adı, alıcı adı, email veya telefon numarası ile ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Durum</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              <option value="all">Tümü</option>
              <option value="awaiting_payment">Ödeme Bekleniyor</option>
              <option value="pending">Beklemede</option>
              <option value="confirmed">Onaylandı</option>
              <option value="preparing">Hazırlanıyor</option>
              <option value="on_the_way">Kurye Yolda</option>
              <option value="delivered">Teslim Edildi</option>
              <option value="cancelled">İptal</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tarih Başlangıcı</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tarih Bitişi</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input-field"
            />
          </div>
        </div>
      </div>

      {/* Select All Bar */}
      {orders.length > 0 && (
        <div className="flex items-center gap-4 bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary transition-colors"
          >
            {allSelected ? (
              <CheckSquare size={20} className="text-primary" />
            ) : (
              <Square size={20} />
            )}
            {allSelected ? 'Seçimi Kaldır' : 'Tümünü Seç'}
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {selectedIds.size} / {orders.length} sipariş seçili
          </span>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin text-4xl">⏳</div>
        </div>
      ) : orders.length === 0 ? (
        <div className="card text-center py-12">
          <Calendar className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-gray-500 dark:text-gray-400">Bu kriterlere uygun sipariş bulunamadı</p>
        </div>
      ) : (
        <Table
          headers={['', 'Sipariş No', 'Alıcı', 'Gönderen', 'Ürün', 'Toplam', 'Durum', 'Tarih']}
          data={tableData}
          renderCell={(item, header) => {
            if (header === '') {
              return (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelect(item.id as string);
                  }}
                  className="p-1"
                >
                  {item.selected ? (
                    <CheckSquare size={20} className="text-primary" />
                  ) : (
                    <Square size={20} className="text-gray-400" />
                  )}
                </button>
              );
            }
            if (header === 'Durum') {
              const status = item.statusRaw as string;
              const color = STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
              return (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${color}`}>
                  {item.status}
                </span>
              );
            }
            const headerMap: Record<string, string> = {
              'Sipariş No': 'orderNo',
              'Alıcı': 'recipient',
              'Gönderen': 'sender',
              'Ürün': 'itemCount',
              'Toplam': 'total',
              'Tarih': 'date',
            };
            const key = headerMap[header];
            return key ? (item[key] as string) : '';
          }}
        />
      )}

      {/* Password Confirmation Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Silme Onayı</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedIds.size} sipariş silinecek
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Bu işlem geri alınamaz. Devam etmek için şifrenizi girin.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Şifre</label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handlePasswordConfirm()}
                placeholder="Şifrenizi girin..."
                className="input-field w-full"
                autoFocus
              />
              {passwordError && (
                <p className="text-red-500 text-sm mt-1">{passwordError}</p>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowPasswordModal(false)}
                disabled={verifying}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handlePasswordConfirm}
                disabled={verifying || !password}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {verifying ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <Trash2 size={16} />
                )}
                {verifying ? 'Doğrulanıyor...' : 'Onayla ve Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {showResultsModal && deleteResults && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Silme Sonuçları</h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-1 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Siparişler</span>
                <span className="font-medium">{deleteResults.orders}</span>
              </div>
              <div className="flex justify-between py-1 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Finansal Kayıtlar</span>
                <span className="font-medium">{deleteResults.orderFinancials}</span>
              </div>
              <div className="flex justify-between py-1 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Ödeme İşlemleri</span>
                <span className="font-medium">{deleteResults.paymentTransactions}</span>
              </div>
              <div className="flex justify-between py-1 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Muhasebe Logları</span>
                <span className="font-medium">{deleteResults.accountingLogs}</span>
              </div>
              <div className="flex justify-between py-1 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Bildirimler</span>
                <span className="font-medium">{deleteResults.notifications}</span>
              </div>
              <div className="flex justify-between py-1 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Kupon Kullanım Logları</span>
                <span className="font-medium">{deleteResults.couponUsageLogs}</span>
              </div>
              <div className="flex justify-between py-1 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Kupon Sayaç Güncellemeleri</span>
                <span className="font-medium">{deleteResults.couponUsedCountUpdates}</span>
              </div>
              <div className="flex justify-between py-1 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Depolama Dosyaları</span>
                <span className="font-medium">{deleteResults.storageFiles}</span>
              </div>
            </div>

            {deleteResults.errors?.length > 0 && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                <p className="text-red-700 dark:text-red-300 text-sm font-medium mb-1">Hatalar:</p>
                <ul className="text-red-600 dark:text-red-400 text-xs space-y-1">
                  {deleteResults.errors.map((err: string, i: number) => (
                    <li key={i}>• {err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowResultsModal(false)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
